// The workspace's plan (spec §14.1-14.2): where it stands, what Team costs,
// and, for owners, subscribing (Stripe Checkout), managing billing (the
// Customer Portal) and switching to Free.
import type { BillingInfo, WorkspaceInfo } from '../../shared/api.ts';
import { formatBytes, formatPrice, FREE_STORAGE_BYTES, TEAM_PRICE, TRIAL_DAYS, type BillingInterval } from '../../shared/billing.ts';
import { ApiError, get, post } from '../api.ts';
import { h } from '../util/dom.ts';
import { busy, daysLeft, formError, getMe, header, longDate, mount, notice, rememberWorkspace, setTitle } from './page.ts';

const CONFIRM_POLL_MS = 2000;
const CONFIRM_TRIES = 15;

export async function planPage(slug: string): Promise<void> {
  const me = await getMe();
  const ws = me.workspaces.find((w) => w.slug === slug);
  if (!ws) throw new ApiError(404, 'Workspace not found.');
  rememberWorkspace(ws.slug);
  setTitle(`Plan · ${ws.name}`);
  const error = formError();
  const body = h('div', { class: 'stack-lg' });
  mount(header(me, ws, 'plan'), h('main', { class: 'page narrow' }, [
    h('div', { class: 'page-head' }, [h('h1', {}, ['Plan'])]), error, body,
  ]));

  // Back from Stripe Checkout: the webhook confirms the payment a moment later.
  const returning = new URLSearchParams(location.search).get('checkout') === 'done';
  let info = await get<BillingInfo>(`/api/workspaces/${ws.id}/billing`);
  render(info, returning && !paid(info));
  if (returning) {
    history.replaceState(null, '', location.pathname);
    for (let i = 0; i < CONFIRM_TRIES && !paid(info); i++) {
      await new Promise((r) => setTimeout(r, CONFIRM_POLL_MS));
      info = await get<BillingInfo>(`/api/workspaces/${ws.id}/billing`);
      render(info, !paid(info));
    }
  }

  function render(b: BillingInfo, confirming: boolean): void {
    body.replaceChildren(
      ...(confirming ? [notice(['Thanks! Your payment is being confirmed. This page updates in a moment.'])] : []),
      current(ws!, b),
      compare(b),
    );
  }

  function current(w: WorkspaceInfo, b: BillingInfo): HTMLElement {
    const actions: HTMLElement[] = [];
    const owner = b.canManage;
    let badge: [string, string] = ['Free', ''];
    let lines: string[] = [];
    switch (b.status === 'none' ? 'free' : b.status) {
      case 'free':
        lines = ['Only the workspace owner edits. Anyone else here can view.',
          b.trialAvailable
            ? `Invite a member from the Members page to start a free ${TRIAL_DAYS}-day Team trial, or subscribe now.`
            : 'Subscribe to Team so everyone you invite can edit.'];
        if (owner) actions.push(...subscribeButtons(b));
        break;
      case 'trialing':
        badge = ['Team trial', 'good'];
        lines = [`${daysLeft(b.trialEndsAt!)} days left: everyone in ${w.name} can edit until ${longDate(b.trialEndsAt!)}.`,
          `If you don't subscribe by then, the workspace becomes read-only. Nothing is deleted.`];
        if (owner) actions.push(...subscribeButtons(b), switchButton('End the trial and switch to Free'));
        break;
      case 'active':
        badge = ['Team', 'good'];
        lines = [`Everyone in ${w.name} can edit. ${b.seats} ${b.seats === 1 ? 'seat' : 'seats'}; seats follow your members, and guests are free.`];
        if (b.cancelAt) lines.push(`Cancelled: Team ends on ${longDate(b.cancelAt)}, then the workspace becomes read-only. You can undo this in billing.`);
        if (owner) actions.push(portalButton('Manage billing'));
        break;
      case 'past_due':
        badge = ['Payment failed', 'warn'];
        lines = ["The last payment didn't go through. Everyone can keep working for now; update the card to keep Team."];
        if (owner) actions.push(portalButton('Update card'));
        break;
      case 'lapsed':
        badge = ['Read-only', 'warn'];
        lines = ['The Team plan has ended, so every board here is read-only. Everyone can still open, view and export them, and nothing has been deleted.',
          'Subscribe to restore editing for everyone, or switch to Free so the owner can edit on their own.'];
        if (owner) actions.push(...subscribeButtons(b), switchButton('Switch to Free'));
        break;
    }
    if (!owner) lines.push('Only the workspace owner can change the plan.');
    else if (!b.billingReady && b.status !== 'active' && b.status !== 'past_due') lines.push("Billing isn't set up on this server yet, so subscribing isn't available.");
    return h('section', { class: 'plan-card' }, [
      h('h2', {}, [w.name, h('span', { class: `plan-badge ${badge[1]}` }, [badge[0]])]),
      ...lines.map((l) => h('p', {}, [l])),
      storage(b),
      ...(actions.length ? [h('div', { class: 'plan-actions' }, actions)] : []),
    ]);
  }

  /** Images and audio on the workspace's boards, against Free's limit (spec §19.6). */
  function storage(b: BillingInfo): HTMLElement {
    if (b.storageLimit === null) return h('p', { class: 'plan-storage' }, [`Storage: ${formatBytes(b.storageUsed)} of images and audio. Team has no storage limit.`]);
    const share = Math.min(1, b.storageUsed / b.storageLimit);
    const full = share >= 0.9;
    const fill = h('span', {});
    fill.style.width = `${(share * 100).toFixed(1)}%`; // a style property, not attribute: the CSP blocks inline style attributes
    return h('div', { class: 'plan-storage' }, [
      h('p', {}, [`Storage: ${formatBytes(b.storageUsed)} of ${formatBytes(b.storageLimit)} used for images and audio.`,
        ...(full ? [' Remove some you no longer need from your boards, or upgrade to Team for unlimited storage.'] : [])]),
      h('div', { class: `storage-meter${full ? ' full' : ''}`, role: 'meter', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(Math.round(share * 100)), 'aria-label': 'Storage used' }, [fill]),
    ]);
  }

  function compare(b: BillingInfo): HTMLElement {
    const seats = b.seats;
    return h('section', { class: 'plan-compare', 'aria-label': 'Plans' }, [
      h('div', { class: 'plan-card' }, [
        h('h3', {}, ['Free']),
        h('p', { class: 'plan-price' }, ['$0']),
        h('ul', {}, [h('li', {}, ['One person edits']), h('li', {}, ['Unlimited boards and every feature']), h('li', {}, ['Share boards for others to view']),
          h('li', {}, [`${formatBytes(b.storageLimit ?? FREE_STORAGE_BYTES)} for images and audio`])]),
      ]),
      h('div', { class: 'plan-card' }, [
        h('h3', {}, ['Team']),
        h('p', { class: 'plan-price' }, [formatPrice(TEAM_PRICE.month), h('small', {}, [' per member / month'])]),
        h('ul', {}, [
          h('li', {}, [`Or ${formatPrice(TEAM_PRICE.year)} per member a year`]),
          h('li', {}, ['Everyone you invite edits together, live']),
          h('li', {}, ['Guests are always free']),
          h('li', {}, ['No storage limit']),
          h('li', {}, [`For ${seats} ${seats === 1 ? 'member' : 'members'} now: ${formatPrice(seats * TEAM_PRICE.month)}/month or ${formatPrice(seats * TEAM_PRICE.year)}/year`]),
        ]),
      ]),
    ]);
  }

  function subscribeButtons(b: BillingInfo): HTMLButtonElement[] {
    const make = (interval: BillingInterval, label: string, primary: boolean) => {
      const btn = h('button', { type: 'button', class: primary ? 'btn primary' : 'btn' }, [label]);
      btn.disabled = !b.billingReady;
      btn.addEventListener('click', () => void busy(btn, async () => {
        error.clear();
        try {
          const { url } = await post<{ url: string }>(`/api/workspaces/${ws!.id}/billing/checkout`, { interval });
          location.assign(url);
        } catch (err) {
          error.show(err);
        }
      }));
      return btn;
    };
    return [
      make('month', `Subscribe monthly · ${formatPrice(TEAM_PRICE.month)}/member`, true),
      make('year', `Subscribe yearly · ${formatPrice(TEAM_PRICE.year)}/member`, false),
    ];
  }

  function portalButton(label: string): HTMLButtonElement {
    const btn = h('button', { type: 'button', class: 'btn primary' }, [label]);
    btn.addEventListener('click', () => void busy(btn, async () => {
      error.clear();
      try {
        location.assign((await post<{ url: string }>(`/api/workspaces/${ws!.id}/billing/portal`)).url);
      } catch (err) {
        error.show(err);
      }
    }));
    return btn;
  }

  function switchButton(label: string): HTMLButtonElement {
    const btn = h('button', { type: 'button', class: 'btn' }, [label]);
    btn.addEventListener('click', () => {
      if (!confirm(`Switch ${ws!.name} to Free? You'll be the only one who can edit; everyone else keeps view access. You can subscribe again at any time.`)) return;
      void busy(btn, async () => {
        error.clear();
        try {
          await post(`/api/workspaces/${ws!.id}/billing/switch-to-free`);
          location.reload();
        } catch (err) {
          error.show(err);
        }
      });
    });
    return btn;
  }
}

const paid = (b: BillingInfo) => b.plan === 'team' && (b.status === 'active' || b.status === 'past_due');
